package ai.jessie.shared.documents

import kotlinx.serialization.Serializable

// UC-020 / FR-020. IDs are public IDs; dates are ISO-8601 dates in the organisation's calendar,
// amounts decimal strings with at most two fraction digits. Every write carries the version the
// client displayed (B9); a stale version is DOCUMENT_CHANGED with the current document (A14).

// An active same-organisation category or folder (A3, BR-132). `active` is false only for the
// document's current, since deactivated choice.
@Serializable
data class DocumentChoiceResponse(
    val id: String,
    val name: String,
    val active: Boolean,
)

// Steps 1-3: the processing detail. No extraction suggestions (B4). `transitions` are the BR-131
// target states this actor may request now; `actions` the other permitted operations
// (SAVE_DETAILS, CLASSIFY, RECORD_PAYMENT, CORRECT_PAYMENT, REMOVE); `editableFields` the fields
// the actor may change (B7). `today` is organisation-local (A6). Source: `.../content` (B8).
@Serializable
data class DocumentDetailResponse(
    val documentId: String,
    val fileName: String,
    val mediaType: String,
    val sizeBytes: Long,
    val uploadedAt: String,
    val version: Long,
    val status: String,
    val details: DocumentDetailsDto,
    val category: DocumentChoiceResponse? = null,
    val folder: DocumentChoiceResponse? = null,
    val paidOn: String? = null,
    val paidAt: String? = null,
    val linkedInvoice: Boolean = false,
    val retentionUntil: String? = null,
    val today: String,
    val transitions: List<String> = emptyList(),
    val actions: List<String> = emptyList(),
    val editableFields: List<String> = emptyList(),
    val categories: List<DocumentChoiceResponse> = emptyList(),
    val folders: List<DocumentChoiceResponse> = emptyList(),
)

// Steps 4-5: the complete editable state. A save or transition sends every value; the server
// changes only differing fields and refuses a change the role may not make (B7).
@Serializable
data class DocumentDetailsDto(
    val title: String,
    val documentType: String? = null,
    val summary: String? = null,
    val externalInvoiceId: String? = null,
    val issuerName: String? = null,
    val recipientName: String? = null,
    val documentDate: String? = null,
    val dueDate: String? = null,
    val currency: String? = null,
    val totalNet: String? = null,
    val totalGross: String? = null,
    val categoryId: String? = null,
    val folderId: String? = null,
    val accountingRelevant: Boolean = false,
)

// B7: the versioned save-details call.
@Serializable
data class DocumentSaveRequest(
    val expectedVersion: Long,
    val details: DocumentDetailsDto,
)

// BR-131 target REVIEW (from Inbox, or reactivation from Archived or Rejected), ACCOUNTING,
// ARCHIVED or REJECTED. `details` are pending edits applied atomically with the transition (B7);
// `reason` is required for rejection (A9) and reactivation (A10).
@Serializable
data class DocumentTransitionRequest(
    val expectedVersion: Long,
    val targetStatus: String,
    val details: DocumentDetailsDto? = null,
    val reason: String? = null,
)

// One recorded field change; values are absent when the field was or became empty.
@Serializable
data class DocumentFieldChangeResponse(
    val field: String,
    val previous: String? = null,
    val new: String? = null,
)

// Step 9: the complete outcome of a transition request, validated but not applied (A13).
@Serializable
data class DocumentTransitionPreviewResponse(
    val documentId: String,
    val version: Long,
    val fromStatus: String,
    val toStatus: String,
    val changes: List<DocumentFieldChangeResponse>,
)

// BR-134: records a new payment or corrects an existing one. An absent `paidOn` means
// organisation-local today.
@Serializable
data class DocumentPaymentRequest(
    val expectedVersion: Long,
    val paidOn: String? = null,
)

// BR-134: clearing an unlinked payment.
@Serializable data class DocumentVersionRequest(val expectedVersion: Long)

// A11 / B6: an eligible document's irreversible removal outcome.
@Serializable
data class DocumentRemovalPreviewResponse(
    val documentId: String,
    val fileName: String,
    val sizeBytes: Long,
    val status: String,
    val version: Long,
)

// A11: the owner's explicit confirmation with a reason.
@Serializable
data class DocumentRemovalRequest(
    val expectedVersion: Long,
    val reason: String,
)

// Step 12 / B5: one recorded document event, oldest first. actorName is absent for a system
// event or a member without a display name.
@Serializable
data class DocumentHistoryEntryResponse(
    val eventType: String,
    val actorName: String? = null,
    val occurredAt: String,
    val changes: List<DocumentFieldChangeResponse> = emptyList(),
)

@Serializable data class DocumentFieldProblem(val field: String, val reason: String)
